import { AddIcon, EastIcon, WChannelIcon, WGroupIcon } from '@weblite/icons'
import { IconButton, styled, Typography } from '@mui/material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { BottomDrawer } from '$common/bottom-drawer'
import { t } from '$i18n'
import { ActionButton } from './action-button'
import { openCreateHall, openSelectWorkspaceName } from '$logic/router/service'
import { useIsVIPPlanEnabledForUser } from '$logic/premium-features/react-hooks'
import { useIsDefaultWorkspace } from '$logic/workspace/react-hooks'
import { CommonOnboarding } from '$logic/onboarding/types'
import { showBuyPremiumFeatureDialog } from '../buy-premium-feature-dialog/buy-premium-feature-dialog.api'
import { PremiumFeatureType } from '../buy-premium-feature-dialog/buy-premium-feature-dialog'

interface Props {
  isOpen?: boolean
}

const onCreateChannel = async () => {
  await closeCurrentModal()
  await openCreateHall('CHANNEL', undefined)
}

const onCreateGroup = async () => {
  await closeCurrentModal()
  await openCreateHall('GROUP')
}

export function CreateCourseDialog({ isOpen }: Props) {
  const isVIPPlanEnabled = useIsVIPPlanEnabledForUser()

  const isCurrentWorkSpaceDefault = useIsDefaultWorkspace()

  const onCreateWorkspace = () => {
    if (isVIPPlanEnabled) openSelectWorkspaceName('')
    else
      showBuyPremiumFeatureDialog({ featureType: PremiumFeatureType.WORKSPACE })
  }
  return (
    <BottomDrawer isOpen={!!isOpen} onClose={closeCurrentModal}>
      <Container>
        <Header>
          <IconButton onClick={closeCurrentModal}>
            <EastIcon sx={{ color: 'neutral500' }} />
          </IconButton>
          <Typography color="neutral900" variant="h3">
            {t('newChatModal.newChat')}
          </Typography>
        </Header>
        <ActionButtonsContainer sx={{ marginBottom: '12px' }}>
          <ActionButton
            icon={
              <IconContainer className="primary">
                <WChannelIcon />
              </IconContainer>
            }
            text={t('newChatModal.createChannel')}
            onClick={onCreateChannel}
            onboardingType={CommonOnboarding.CREATE_COURSE}
            doCompleteOnboarding={false}
            description={t('newChatModal.createChannelDescription')}
          />
          <ActionButton
            icon={
              <IconContainer className="secondary">
                <WGroupIcon />
              </IconContainer>
            }
            text={t('newChatModal.createGroup')}
            onClick={onCreateGroup}
            description={t('newChatModal.createGroupDescription')}
            isLast
          />
        </ActionButtonsContainer>
        {isCurrentWorkSpaceDefault && (
          <ActionButtonsContainer>
            <ActionButton
              icon={
                <IconContainer className="success">
                  <AddIcon />
                </IconContainer>
              }
              text={t('newChatModal.createWorkspace')}
              onClick={onCreateWorkspace}
              description={t('newChatModal.createWorkspaceDescription')}
              isLast
            />
          </ActionButtonsContainer>
        )}
      </Container>
    </BottomDrawer>
  )
}

const Container = styled('div')`
  background-color: ${({ theme }) => theme.palette.neutral100};
`

const Header = styled('div')`
  height: 56px;
  display: flex;
  align-items: center;
  gap: 2px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  padding-right: 8px;
`

const ActionButtonsContainer = styled('div')`
  background-color: ${({ theme }) => theme.palette.neutral000};
  box-shadow: ${({ theme }) => theme.shadows[3]};
`

const IconContainer = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  &.primary {
    color: ${({ theme }) => theme.palette.primary500};
    background-color: ${({ theme }) => theme.palette.primary50};
  }
  &.secondary {
    color: ${({ theme }) => theme.palette.secondary500};
    background-color: ${({ theme }) => theme.palette.secondary50};
  }
  &.success {
    color: ${({ theme }) => theme.palette.success500};
    background-color: ${({ theme }) => theme.palette.success100};
  }
  svg {
    width: 24px;
    height: 24px;
    color: inherit;
  }
`
